import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import { getCompReference } from "../state/objects/comp";
import { copyShip, delShip, removeShipTag, selectCurrentProject, selectCurrentProjectComponents, selectShip, swapShips, tagShip, upsertShip } from "../state/slices/systemSlice";
import { faAngleDown, faAngleUp, faArrowRightArrowLeft, faCopy, faTag, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Ship, ShipFieldsDictionary } from "../state/objects/ship";
import { MutableRefObject, useCallback, useRef, useState } from "react";
import OptionalSelect from "./optionalSelect";
import ShipsInput from "./ShipsInput";
import ColorPicker from "./ColorPicker";
import { localconsole } from "../App";
import { useDrag, useDrop } from "react-dnd";

const DraggableShip = (props: {shipKey:string, isActive:boolean, shipId: string, setActive: () => void, setInactive: () => void}) => {
    const dispatch = useAppDispatch()
    const components = useAppSelector(state => selectCurrentProjectComponents(state))
    const currentProject = useAppSelector(state => selectCurrentProject(state));
    const items = useAppSelector(state => currentProject.ships)
    const ship = useAppSelector(state => selectShip(props.shipId)(state))
    const [direction, setDirection] = useState<boolean>(false);
    const comp_names: string[] = Object.entries(components).map(([s, c], i) => getCompReference(c) ?? "").filter((s) => {return s !== ""})


    let from_columns = Object.values(components).find((c, i) => getCompReference(c) === ship?.from)?.columns;
    let to_columns = Object.values(components).find((c, i) => getCompReference(c) === ship?.to)?.columns;


    //cancelling drag and drop
    const originalIndex = props.shipKey

    //dnd functions
    const findItem = useCallback(
        (id: string) => {
            //localconsole(`findItem: ${id}`)
        const keyItem = Object.entries(items).filter(([i, c]) => c.id == id)[0]
        const item = keyItem[1]
        return {
            item,
            index: item.id,
        }
        },
        [items],
    )

    const moveItem = useCallback(
        (id: string, overId: string, insertBefore: boolean) => {
            localconsole(`move ${id} ${insertBefore ? 'before' : 'after'} ${overId}`)
        
        dispatch(swapShips({from: id, to: overId, insertBefore: insertBefore}))
        
        },
        [items],
    )

    const [{ isDragging }, drag] = useDrag(
    () => ({
    type: 'ship',
    item: { id: props.shipId, itemKey: originalIndex },
    canDrag: (monitor) => {
            return !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName ?? "")
        },
    collect: (monitor) => ({
        isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
        //lastTargetIndex.current = null
        const { id: droppedId, itemKey } = item
        const didDrop = monitor.didDrop()
        if (!didDrop) {
            //revert the item to original position
            localconsole(`revert ${droppedId} to ${itemKey}`)
            let keys = Object.keys(items)
            const fromIndex = keys.findIndex(k => items[k].id === droppedId);
            const toIndex = keys.findIndex(k => k === itemKey);

            //direction
            const direction = fromIndex < toIndex ? false : true;

            moveItem(droppedId, items[itemKey].id , direction);
            
        }
    },
    }),
    [props.shipId, originalIndex, moveItem, items],
)

const ref: MutableRefObject<HTMLDivElement | null> = useRef(null)

const [, drop] = useDrop(
        () => ({
        accept: 'ship',
        hover({ id }: { id: string }, monitor) {
            //localconsole(`Hovering over ${id}`)

            //don't move over itself
            if (id === props.shipId) return;

            const overIndex = props.shipId;
            const dragIndex = id;

            //don't move if already switched
            if (dragIndex === overIndex) return;

            //don't jump back and forth
            //if (lastTargetIndex.current === overIndex) return;

            //lastTargetIndex.current = overIndex;

            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            if (!hoverBoundingRect) return
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (hoverClientY > hoverMiddleY) {
                // insert after
                localconsole(`Below`)
                moveItem(dragIndex, overIndex, false)
            } else {
                // insert before
                localconsole(`Above`)
                moveItem(dragIndex, overIndex, true)
            }
            
            
        },
        }),
        [findItem, moveItem],
    )
    const setRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
        drag(drop(node))
        ref.current = node // store DOM for rect access
    }
    }, [drag, drop])


    const addGrey = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!props.isActive) {
        e.currentTarget.classList.add("has-background-grey-lighter")
    }
    }
    const removeGrey = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.currentTarget.classList.remove("has-background-grey-lighter")
    }
    const binShip = (item_id: string) => {
        dispatch(delShip(item_id))
    }

    const cloneShip = (item_id: string) => {
        dispatch(copyShip(item_id))
    }
    const addTag = (ship_id: string) => {
        //e.currentTarget.classList.remove("pulse");
        let input = window.prompt("Add Tag:");
        if (input){
            dispatch(tagShip({ship_id: ship_id, tag: input}))
        }
    }
    const activeShipTags = ship?.tags?.map((tag, i) => {
        return (<div className="control" key={`t_${i}`}>
                    <div className="tags has-addons">
                        <a className="tag">{`${tag}`}</a>
                        <a className="tag is-delete" onClick={(e) => {
                            dispatch(removeShipTag({ship_id: ship.id, tag: tag}))
                        }}></a>
                    </div>
                </div>)
    })
    const toggleRow = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    removeGrey(e)
    if(props.isActive) {
        props.setInactive()
    } else {
        props.setActive()
    }
    }
    function cardinalityDisplay(cardinality: string) {
    let ret;
    switch (cardinality) {
        case "1:1":
            ret = "―"
            break;
        case "1:M":
            ret = "ᗕ"
            break;
        case "M:1":
            ret = "ᗒ"
            break;
        case "ExO":
            ret = "Exactly One"
            break;
        case "OoM":
            ret = "One or Many"
            break;
        case "ZoO":
            ret = "Zero or One"
            break;
        case "ZoM":
            ret = "Zero or Many"
            break;
        case "Aro":
            ret = "→"
            break;
        case "Dot":
            ret = "⋯"
            break;
        case "---":
            ret = "┄"
            break;
        case "Cmp":
            ret = "Composition"
            break;
        case "Agg":
            ret = "Aggregation"
            break;
        case "Ext":
            ret = "Extension"
            break;
        case "Imp":
            ret = "Implements"
            break;
        default:
            ret = "1:1"
            break;
    }
    return ret;

    }

   

    return (!ship) ? null : (
    /*Do not include the data in the key or you cannot type into inputs*/
    <div className={"card is-shadowless"}

            key={`${props.shipId}_${direction}`} data-key={props.shipId}
            style={{opacity: isDragging ? 0 : 1}}
            
    >
    <header className={"card-header" +
        ((props.isActive) ? " has-background-primary has-text-light" : "")
        }
        onClick={toggleRow}
        onMouseEnter={addGrey}
        onMouseLeave={removeGrey}
        onDragStart={e => props.setInactive()}
        ref={!props.isActive ? setRef : undefined}
    >

        <p className={"card-header-title" +
            " has-text-weight-light" +
                ((props.isActive) ? " has-text-light " : "")
            }
        >
            <span>{ship.from}</span>
            <span className={"px-1"}>{cardinalityDisplay(ship.cardinality)}</span>
            <span>{ship.to}</span>
        </p>

        {(props.isActive) ? <>
                <button className={"card-header-icon "}
                        onClick={(e) => {
                            e.stopPropagation();
                            binShip(ship.id)
                        }}
                        title={"Delete Relationship"}
                >
                    <span className={"icon"}>
                        <FontAwesomeIcon icon={faTrash}/>
                    </span>
                </button>
                <button className={"card-header-icon  "} onClick={(e) => {
                    e.stopPropagation();
                    cloneShip(ship.id)
                }}
                        title={"Copy Relationship"}
                >
                <span className={"icon"}>
                    <FontAwesomeIcon icon={faCopy}/>
                </span>
                </button>

                <button className={"card-header-icon"}
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(upsertShip({
                                ...ship,
                                from: ship.to,
                                to: ship.from,
                            } as Ship))
                            //re-render inputs by flipping direction
                            setDirection(!direction)
                        }}
                        title={"Reverse Relationship"}
                >
                    <span className={"icon"}>
                        <FontAwesomeIcon icon={faArrowRightArrowLeft}/>
                    </span>
                </button>
                <button className={"card-header-icon"} aria-label="add tag"
                    onClick={(e) => {
                        e.stopPropagation();
                        addTag(ship.id)
                    }}
                    title={"Tag Relationship"}
                >
                    <span className={"icon"}>
                        <FontAwesomeIcon icon={faTag}/>
                    </span>
                </button>
            </>
            : null}
        <button className="card-header-icon" aria-label="more options">
            <span className="icon">
            {(props.isActive) ? <FontAwesomeIcon icon={faAngleUp}/> : <FontAwesomeIcon icon={faAngleDown}/>}
            </span>
        </button>
    </header>
    {(props.isActive) ?
        <div className={"card-content"}>
            <div className={"block"}>

            {(ship.tags?.length > 0) ? <div className="field is-horizontal">
                            <div className="field-label">
                                <label className="label">Tags</label>
                            </div>
                            <div className="field-body">
                            <div className="field is-grouped is-grouped-multiline">
                                {activeShipTags}
                        </div>
                    </div>
                </div>:<div></div>}

                <div className="field is-horizontal">
                <div className="field-label is-normal">
                    <label className="label">From</label>
                </div>
                <div className="field-body">
                    <div className="field has-addons">
                        <div className="control is-expanded">
                            
                            <OptionalSelect options={comp_names} placeholder={"Select an option"}
                                            value={ship.from}
                                            onSelection={(selection: string) => {
                                let newRecord: Ship = {
                                    ...ship,
                                    from: selection
                                }
                                dispatch(upsertShip(newRecord))
                            }}/>
                        </div>
                        
                    </div>
                </div>
            </div>

            <div className="field is-horizontal">
                <div className="field-label is-normal">
                    <label className="label">To</label>
                </div>
                <div className="field-body">
                    <div className="field">
                        <div className="control">

                            <OptionalSelect options={comp_names} placeholder={"Select an option"} value={ship.to} onSelection={(selection: string) => {
                                let newRecord: Ship = {
                                    ...ship,
                                    to: selection
                                }
                                dispatch(upsertShip(newRecord))
                            }}/>

                            {/*<ShipsInput record={ship} field={ShipFieldsDictionary.to} setRecord={(c) => dispatch(addShip(c))} activateRecord={activateRow} />*/}
                        </div>
                    </div>
                </div>
            </div>

            <div className="field is-horizontal">
                <div className="field-label is-normal">
                    <label className="label">Cardinality</label>
                </div>
                <div className="field-body">
                    <div className="field">
                        <div className="control">
                            <div className="field" key={"Cardinality"}>


                                <div className="control">
                                    <div className="select is-fullwidth">
                                        <select id={"rel_select_" + ship.id}
                                                onChange={(e) => dispatch(upsertShip({...ship, cardinality: e.currentTarget.value}))}
                                                defaultValue={ship.cardinality}

                                        >
                                            <optgroup label="Relational">
                                                <option value={"1:1"}>{cardinalityDisplay("1:1")}</option>
                                                <option value={"1:M"}>{cardinalityDisplay("1:M")}</option>
                                                <option value={"M:1"}>{cardinalityDisplay("M:1")}</option>
                                                <option value={"ExO"}>{cardinalityDisplay("ExO")}</option>
                                                <option value={"OoM"}>{cardinalityDisplay("OoM")}</option>
                                                <option value={"ZoO"}>{cardinalityDisplay("ZoO")}</option>
                                                <option value={"ZoM"}>{cardinalityDisplay("ZoM")}</option>
                                            </optgroup>
                                            <optgroup label="Annotation">
                                                <option value={"Aro"}>{cardinalityDisplay("Aro")}</option>
                                                <option value={"Dot"}>{cardinalityDisplay("Dot")}</option>
                                                <option value={"---"}>{cardinalityDisplay("---")}</option>
                                                <option value={"Cmp"}>{cardinalityDisplay("Cmp")}</option>
                                                <option value={"Agg"}>{cardinalityDisplay("Agg")}</option>
                                                <option value={"Ext"}>{cardinalityDisplay("Ext")}</option>
                                                <option value={"Imp"}>{cardinalityDisplay("Imp")}</option>
                                                {/*<option value={"==="}>===</option>*/}
                                            </optgroup>
                                        </select>
                                    </div>


                                    {/*<label className="radio">
                                        <input type="radio" name="Cardinality"

                                                onChange={(e) => dispatch(addShip({...ship, cardinality: e.currentTarget.value}))}
                                        ></input>
                                        1:1
                                    </label>
                                    <label className="radio">
                                        <input type="radio" name="Cardinality"

                                                onChange={(e) => dispatch(addShip({...ship, cardinality: e.currentTarget.value}))}
                                        ></input>
                                        1:M
                                    </label>
                                    <label className="radio">
                                        <input type="radio" name="Cardinality"

                                                onChange={(e) => dispatch(addShip({...ship, cardinality: e.currentTarget.value}))}
                                        ></input>
                                        M:1
                                    </label>
                                </div>
                                <div className="control">
                                    <label className="radio">
                                        <input type="radio" name="Cardinality"

                                                onChange={(e) => dispatch(addShip({...ship, cardinality: e.currentTarget.value}))}
                                        ></input>
                                        Exactly One
                                    </label>
                                    <label className="radio">
                                        <input type="radio" name="Cardinality"
                                                value={""}
                                                onChange={(e) => dispatch(addShip({...ship, cardinality: e.currentTarget.value}))}
                                        ></input>
                                        Arrow
                                    </label>

                                    <label className="radio">
                                        <input type="radio" name="Cardinality"
                                                value={""}
                                                onChange={(e) => dispatch(addShip({...ship, cardinality: e.currentTarget.value}))}
                                        ></input>
                                        <span>One or Many</span>
                                    </label>
                                    <label className="radio">
                                        <input type="radio" name="Cardinality"
                                                value={""}
                                                onChange={(e) => dispatch(addShip({...ship, cardinality: e.currentTarget.value}))}
                                        ></input>
                                        Dotted
                                    </label>*/}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="field is-horizontal">
                <div className="field-label is-normal">
                    <label className="label">From Col</label>
                </div>
                <div className="field-body">
                    <div className="field">
                        <div className="control">
                            {/*<ShipsInput record={ship} field={ShipFieldsDictionary.fromCol} setRecord={(c) => dispatch(addShip(c))} activateRecord={activateRow} />*/}
                            <OptionalSelect options={from_columns?.map((c) => c.name ?? c.db_name ?? undefined) ?? []} placeholder={"Select an option"} value={ship.from_col ?? ""}
                                            onSelection={(selection: string) => {
                                let newRecord: Ship = {
                                    ...ship,
                                    from_col: selection
                                }
                                dispatch(upsertShip(newRecord))
                            }}/>
                        </div>
                    </div>
                </div>
            </div>

            <div className="field is-horizontal">
                <div className="field-label is-normal">
                    <label className="label">To Col</label>
                </div>
                <div className="field-body">
                    <div className="field">
                        <div className="control">
                            {/*<ShipsInput record={ship} field={ShipFieldsDictionary.toCol} setRecord={(c) => dispatch(addShip(c))} activateRecord={activateRow} />*/}
                            <OptionalSelect options={to_columns?.map((c) => c.name ?? c.db_name ?? "") ?? []} placeholder={"Select an option"} value={ship.to_col ?? ""}
                                            onSelection={(selection: string) => {
                                let newRecord: Ship = {
                                    ...ship,
                                    to_col: selection
                                }
                                dispatch(upsertShip(newRecord))
                            }}/>
                        </div>
                    </div>
                </div>
            </div>

            <div className="field is-horizontal">
                <div className="field-label is-normal">
                    <label className="label">Freestyle</label>
                </div>
                <div className="field-body">
                    <div className="field">
                        <p className="control">
                            <ShipsInput record={ship} field={ShipFieldsDictionary.freestyle} setRecord={(c) => dispatch(upsertShip(c))} activateRecord={() => {props.setActive()}} />
                        </p>
                    </div>
                </div>
            </div>

            <ColorPicker color={(ship.colors ?? {})["Line"] ?? "#808080"} setColor={(kv) => {
                                let c: Ship = {
                                    ...ship,
                                    colors: {...ship.colors}  //clone so I can modify
                                };
                                (c.colors ?? {})[kv.key] = kv.value
                                dispatch(upsertShip(c))
                            }}
                                name={"Line"}
                                label={"Line"}
                                boldLabel={true}
            />
        </div>
    </div>:<div></div>}
</div>
)


}

export default DraggableShip


